/**
 * TypeScript assertion function for exhaustive checking in switch statements and union type handling.
 *
 * This function ensures that all possible cases in a discriminated union have been handled.
 * When used in the `default` case of a switch statement or the final branch of conditional logic,
 * it will cause a TypeScript compilation error if there are unhandled cases.
 *
 * @param value - The value that should never be reached. TypeScript will enforce that this
 *                parameter has type `never`, meaning all possible cases have been exhausted.
 * @returns Never returns - this function always throws an error if reached at runtime.
 *
 * @throws {Error} Throws an error with details about the unexpected value if this function
 *                 is ever actually called at runtime, which indicates a bug in the code.
 *
 * @example
 * ```typescript
 * type Theme = "light" | "dark" | "system";
 *
 * function getThemeIcon(theme: Theme): string {
 *   switch (theme) {
 *     case "light":
 *       return "☀️";
 *     case "dark":
 *       return "🌙";
 *     case "system":
 *       return "🔄";
 *     default:
 *       // TypeScript will error if we add a new theme type but forget to handle it above
 *       return assertNever(theme);
 *   }
 * }
 * ```
 *
 * @example
 * ```typescript
 * // If you add a new theme type like this:
 * type Theme = "light" | "dark" | "system" | "auto";
 *
 * // The function above will now show a TypeScript error at the assertNever call,
 * // forcing you to handle the new "auto" case
 * ```
 */
export const assertNever = (value: never): never => {
  throw new Error(
    `Unexpected value received: ${JSON.stringify(value)}. This should never happen and indicates a bug in the exhaustive checking.`,
  );
};
